import type { FilterConfig } from '@/types/filters'

/**
 * LOTE 6: Configurações de filtros para indicadores e métricas de performance
 * 
 * INDICADORES DE PERFORMANCE (4 campos):
 * - Score de Performance
 * - Última Avaliação
 * - Data da Última Avaliação
 * - Meta Atingida (%)
 * 
 * MÉTRICAS DE ENGAJAMENTO (4 campos):
 * - NPS do Colaborador
 * - Participação em Treinamentos (%)
 * - Feedback Score
 * - Tempo Médio de Resposta (horas)
 */

export const LOTE_6_FILTER_CONFIGS: FilterConfig[] = [
  // ===== INDICADORES DE PERFORMANCE =====
  {
    columnId: 'performanceScore',
    label: 'Score de Performance',
    type: 'number',
    operators: ['equals', 'greaterThan', 'lessThan', 'greaterThanOrEqual', 'lessThanOrEqual', 'between', 'isEmpty', 'isNotEmpty'],
    placeholder: 'Ex: 85',
    category: 'Performance'
  },
  {
    columnId: 'lastEvaluationResult',
    label: 'Última Avaliação',
    type: 'select',
    operators: ['equals', 'in', 'notIn'],
    options: [
      { value: 'exceeds_expectations', label: 'Supera Expectativas' },
      { value: 'meets_expectations', label: 'Atende Expectativas' },
      { value: 'partially_meets', label: 'Atende Parcialmente' },
      { value: 'below_expectations', label: 'Abaixo das Expectativas' },
      { value: 'needs_improvement', label: 'Precisa Melhorar' },
      { value: 'not_evaluated', label: 'Não Avaliado' }
    ],
    placeholder: 'Selecione a avaliação...',
    category: 'Performance'
  },
  {
    columnId: 'lastEvaluationDate',
    label: 'Data da Última Avaliação',
    type: 'date',
    operators: ['equals', 'greaterThan', 'lessThan', 'between', 'isEmpty', 'isNotEmpty'],
    placeholder: 'dd/mm/aaaa',
    category: 'Performance'
  },
  {
    columnId: 'goalAchievementPercent',
    label: 'Meta Atingida (%)',
    type: 'number',
    operators: ['equals', 'greaterThan', 'lessThan', 'greaterThanOrEqual', 'lessThanOrEqual', 'between'],
    placeholder: 'Ex: 95',
    category: 'Performance'
  },
  
  // ===== MÉTRICAS DE ENGAJAMENTO =====
  {
    columnId: 'employeeNPS',
    label: 'NPS do Colaborador',
    type: 'select',
    operators: ['equals', 'in', 'notIn', 'greaterThan', 'lessThan'],
    options: [
      { value: 'promoter', label: 'Promotor (9-10)' },
      { value: 'passive', label: 'Neutro (7-8)' },
      { value: 'detractor', label: 'Detrator (0-6)' },
      { value: 'not_surveyed', label: 'Não Pesquisado' }
    ],
    placeholder: 'Selecione o NPS...',
    category: 'Engajamento'
  },
  {
    columnId: 'trainingParticipationRate',
    label: 'Participação em Treinamentos (%)',
    type: 'number',
    operators: ['equals', 'greaterThan', 'lessThan', 'greaterThanOrEqual', 'lessThanOrEqual', 'between'],
    placeholder: 'Ex: 80',
    category: 'Engajamento'
  },
  {
    columnId: 'feedbackScore',
    label: 'Feedback Score',
    type: 'number',
    operators: ['equals', 'greaterThan', 'lessThan', 'greaterThanOrEqual', 'lessThanOrEqual', 'between', 'isEmpty', 'isNotEmpty'],
    placeholder: 'Ex: 4.2',
    category: 'Engajamento'
  },
  {
    columnId: 'avgResponseTime',
    label: 'Tempo Médio de Resposta (horas)',
    type: 'number',
    operators: ['equals', 'greaterThan', 'lessThan', 'greaterThanOrEqual', 'lessThanOrEqual', 'between'],
    placeholder: 'Ex: 24',
    category: 'Engajamento'
  }
]

/**
 * Acessores para campos do Lote 6
 * Campos que podem precisar de cálculos ou transformações
 */
export const LOTE_6_FIELD_ACCESSORS = {
  // Performance - campos diretos ou calculados
  performanceScore: (item: any) => {
    // Se já existe o campo calculado, usar ele
    if (item.performanceScore !== undefined) return item.performanceScore
    
    // Senão, calcular da última avaliação
    const evaluations = item.performanceEvaluations || []
    if (!evaluations.length) return null
    
    const sortedEvaluations = [...evaluations].sort((a: any, b: any) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    )
    
    return sortedEvaluations[0]?.score || null
  },
  
  lastEvaluationResult: (item: any) => {
    // Se já existe o campo calculado, usar ele
    if (item.lastEvaluationResult !== undefined) return item.lastEvaluationResult
    
    // Senão, calcular da última avaliação
    const evaluations = item.performanceEvaluations || []
    if (!evaluations.length) return 'not_evaluated'
    
    const sortedEvaluations = [...evaluations].sort((a: any, b: any) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    )
    
    return sortedEvaluations[0]?.result || 'not_evaluated'
  },
  
  lastEvaluationDate: (item: any) => {
    // Se já existe o campo calculado, usar ele
    if (item.lastEvaluationDate !== undefined) return item.lastEvaluationDate
    
    // Senão, calcular da última avaliação
    const evaluations = item.performanceEvaluations || []
    if (!evaluations.length) return null
    
    const sortedEvaluations = [...evaluations].sort((a: any, b: any) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    )
    
    return sortedEvaluations[0]?.date || null
  },
  
  goalAchievementPercent: (item: any) => {
    // Se já existe o campo calculado, usar ele
    if (item.goalAchievementPercent !== undefined) return item.goalAchievementPercent
    
    // Senão, calcular das metas
    const goals = item.goals || []
    if (!goals.length) return null
    
    const activeGoals = goals.filter((goal: any) => goal.status === 'active')
    if (!activeGoals.length) return null
    
    const totalGoals = activeGoals.length
    const achievedGoals = activeGoals.filter((goal: any) => goal.achieved).length
    
    return Math.round((achievedGoals / totalGoals) * 100)
  },
  
  // Engajamento - campos diretos ou calculados
  employeeNPS: (item: any) => {
    // Se já existe o campo calculado, usar ele
    if (item.employeeNPS !== undefined) return item.employeeNPS
    
    // Senão, calcular da última pesquisa de NPS
    const surveys = item.npsSurveys || []
    if (!surveys.length) return 'not_surveyed'
    
    const latestSurvey = surveys.sort((a: any, b: any) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    )[0]
    
    const score = latestSurvey?.score
    if (score === undefined || score === null) return 'not_surveyed'
    
    if (score >= 9) return 'promoter'
    if (score >= 7) return 'passive'
    return 'detractor'
  },
  
  trainingParticipationRate: (item: any) => {
    // Se já existe o campo calculado, usar ele
    if (item.trainingParticipationRate !== undefined) return item.trainingParticipationRate
    
    // Senão, calcular dos treinamentos
    const trainings = item.trainings || []
    const totalTrainings = trainings.length
    
    if (totalTrainings === 0) return null
    
    const completedTrainings = trainings.filter((training: any) => training.status === 'completed').length
    return Math.round((completedTrainings / totalTrainings) * 100)
  },
  
  feedbackScore: (item: any) => {
    // Se já existe o campo calculado, usar ele
    if (item.feedbackScore !== undefined) return item.feedbackScore
    
    // Senão, calcular dos feedbacks
    const feedbacks = item.feedbacks || []
    if (!feedbacks.length) return null
    
    const validFeedbacks = feedbacks.filter((feedback: any) => 
      feedback.score !== undefined && feedback.score !== null
    )
    
    if (!validFeedbacks.length) return null
    
    const totalScore = validFeedbacks.reduce((sum: number, feedback: any) => sum + feedback.score, 0)
    return Math.round((totalScore / validFeedbacks.length) * 10) / 10 // Arredondar para 1 casa decimal
  },
  
  avgResponseTime: (item: any) => {
    // Se já existe o campo calculado, usar ele
    if (item.avgResponseTime !== undefined) return item.avgResponseTime
    
    // Senão, calcular dos tempos de resposta
    const responses = item.responsesTimes || []
    if (!responses.length) return null
    
    const validResponses = responses.filter((response: any) => 
      response.timeInHours !== undefined && response.timeInHours !== null
    )
    
    if (!validResponses.length) return null
    
    const totalTime = validResponses.reduce((sum: number, response: any) => sum + response.timeInHours, 0)
    return Math.round(totalTime / validResponses.length)
  }
}

/**
 * Presets estratégicos para Lote 6
 */
export const LOTE_6_PRESETS = [
  {
    id: 'high_performers',
    name: 'High Performers',
    description: 'Colaboradores com alta performance e engajamento',
    filters: [
      {
        columnId: 'activityStatus',
        operator: 'equals' as const,
        value: 'active',
        type: 'select' as const,
        label: 'Status de Atividade',
        enabled: true
      },
      {
        columnId: 'performanceScore',
        operator: 'greaterThanOrEqual' as const,
        value: 85,
        type: 'number' as const,
        label: 'Score de Performance',
        enabled: true
      },
      {
        columnId: 'lastEvaluationResult',
        operator: 'in' as const,
        value: ['exceeds_expectations', 'meets_expectations'],
        type: 'multiselect' as const,
        label: 'Última Avaliação',
        enabled: true
      }
    ],
    isSystem: true
  },
  {
    id: 'needs_attention',
    name: 'Precisam de Atenção',
    description: 'Colaboradores com performance ou engajamento baixo',
    filters: [
      {
        columnId: 'activityStatus',
        operator: 'equals' as const,
        value: 'active',
        type: 'select' as const,
        label: 'Status de Atividade',
        enabled: true
      },
      {
        columnId: 'lastEvaluationResult',
        operator: 'in' as const,
        value: ['below_expectations', 'needs_improvement', 'partially_meets'],
        type: 'multiselect' as const,
        label: 'Última Avaliação',
        enabled: true
      }
    ],
    isSystem: true
  },
  {
    id: 'promoters',
    name: 'Colaboradores Promotores',
    description: 'Colaboradores com NPS alto (promotores)',
    filters: [
      {
        columnId: 'activityStatus',
        operator: 'equals' as const,
        value: 'active',
        type: 'select' as const,
        label: 'Status de Atividade',
        enabled: true
      },
      {
        columnId: 'employeeNPS',
        operator: 'equals' as const,
        value: 'promoter',
        type: 'select' as const,
        label: 'NPS do Colaborador',
        enabled: true
      }
    ],
    isSystem: true
  },
  {
    id: 'training_champions',
    name: 'Champions de Treinamento',
    description: 'Colaboradores com alta participação em treinamentos',
    filters: [
      {
        columnId: 'activityStatus',
        operator: 'equals' as const,
        value: 'active',
        type: 'select' as const,
        label: 'Status de Atividade',
        enabled: true
      },
      {
        columnId: 'trainingParticipationRate',
        operator: 'greaterThanOrEqual' as const,
        value: 80,
        type: 'number' as const,
        label: 'Participação em Treinamentos (%)',
        enabled: true
      }
    ],
    isSystem: true
  },
  {
    id: 'recent_evaluations',
    name: 'Avaliações Recentes',
    description: 'Colaboradores avaliados nos últimos 6 meses',
    filters: [
      {
        columnId: 'activityStatus',
        operator: 'equals' as const,
        value: 'active',
        type: 'select' as const,
        label: 'Status de Atividade',
        enabled: true
      },
      {
        columnId: 'lastEvaluationDate',
        operator: 'greaterThan' as const,
        value: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 6 meses atrás
        type: 'date' as const,
        label: 'Data da Última Avaliação',
        enabled: true
      }
    ],
    isSystem: true
  }
] as const